
import _ from 'lodash'
import moment from 'moment'
import PropTypes from 'prop-types'
import { toast } from 'react-toastify'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import SubmitButton from '../../../components/common/buttons/SubmitButton'
import LoadingSpinner from '../../common/loader/legacy-loaders/LoadingSpinner'
import CoachOnboardingStepsAdminModal from './CoachOnboardingAdmin/CoachOnboardingStepsAdminModal'

import { requestAdminGetUserSettings } from '../../../actions/userActions'
import { requestCompleteOnboarding, requestGetCoachOnboardingSteps, requestResetOnboarding } from '../../../actions/coachOnboardingActions'
import { deserialize } from '../../../utility/serialization'


const CoachOnboardingDetails = ({ coachId }) => {
  const { t } = useTranslation()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [onboardingSteps, setOnboardingSteps] = useState([])
  const [userSettings, setUserSettings] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  const [isStatusUpdating, setIsStatusUpdating] = useState(false)

  const updateStatus = () => {
    const requestUpdateOnboardingStatus = {
      reset: requestResetOnboarding,
      complete: requestCompleteOnboarding,
    }[userSettings?.coachOnboardingStatus === 'started' ? 'complete' : 'reset']

    setIsStatusUpdating(true)
    requestUpdateOnboardingStatus(coachId)
      .then(fetchDetails)
      .catch(() => {
        toast.error(t('communities.onboarding.toast.error'), { toastId: 'onboarding-status-update' })
      })
      .finally(() => setIsStatusUpdating(false))
  }

  const fetchDetails = useCallback(() => {
    setIsLoading(true)
    Promise.all([requestGetCoachOnboardingSteps(coachId), requestAdminGetUserSettings(coachId)])
      .then(([stepsResponse, settingsResponse]) => {
        const stepsData = deserialize(stepsResponse.data)
        const settingsData = deserialize(settingsResponse.data)

        setOnboardingSteps(stepsData.onboardingSteps)
        setUserSettings(settingsData.userSetting)
      })
      .finally(() => setIsLoading(false))
  }, [coachId])

  useEffect(() => {
    fetchDetails()
  }, [fetchDetails])

  const { completedSteps, totalSteps } = useMemo(
    () => {
      const items = onboardingSteps.flatMap(step => step.coachOnboardingItems)
      return { completedSteps: items.filter(item => item.completed).length, totalSteps: items.length }
    },
    [onboardingSteps]
  )

  const onboardingStatus = _.capitalize(userSettings?.coachOnboardingStatus)
  const lastModifiedDate = moment(userSettings?.coachOnboardingLastModified).format('L')
  const lastModifiedRelativeTime = moment(userSettings?.coachOnboardingLastModified).fromNow()
  const lastModified = `${lastModifiedDate}, ${lastModifiedRelativeTime}`

  return (
    <>
      <div className='coach-onboarding-details'>
        <div className='d-flex justify-content-between mb-3'>
          <h6 className='onboarding-section-title'>{t('communities.onboarding.onboardingStatus')}</h6>
          <button
            onClick={() => setIsModalOpen(true)}
            className='onboarding-details-button'
            disabled={isLoading || onboardingSteps.length === 0}
          >
            {t('common.action.viewDetails')}
            {isLoading && (
              <LoadingSpinner classNames={{ wrapper: 'mt-0', spinner: 'fa-lg' }} />
            )}
          </button>
        </div>
        <div className='d-flex align-items-center justify-content-between'>
          <p className='onboarding-status-text'>{isLoading ? t('communities.onboarding.loadingLower') : `${onboardingStatus} ${lastModified}`}</p>
          <p className='total-count'>
            <Trans
              i18nKey='communities.onboarding.completeCount'
              values={{ completed: completedSteps, total: totalSteps }}
              components={{ completedSpan: <span className='completed-count' /> }}
            />
          </p>
        </div>
        <SubmitButton
          onClick={updateStatus}
          className='btn btn-primary onboarding-action-button'
          buttonMessage={
            userSettings?.coachOnboardingStatus === 'started'
              ? t('communities.onboarding.completeTraining')
              : t('communities.onboarding.resetTraining')
          }
          loading={isStatusUpdating}
        />
      </div>
      <CoachOnboardingStepsAdminModal
        onboardingSteps={onboardingSteps}
        onboardingStatus={onboardingStatus}
        lastModified={lastModified}
        completedSteps={completedSteps}
        totalSteps={totalSteps}
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  )
}

CoachOnboardingDetails.propTypes = {
  coachId: PropTypes.number,
}

export default CoachOnboardingDetails
